import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "uploadButton",
    "fileInput", 
    "uploadProgress",
    "fileName",
    "progressBar",
    "progressText", 
    "progressPercentage",
    "fileInfo",
    "selectedFileName",
    "selectedFileSize",
    "uploadError",
    "errorMessage",
    "submitButton",
    "form"
  ]

  static values = {
    directUploadUrl: String,
    inputName: String
  }

  connect() {
    this.selectedFiles = []
    this.uploadedBlobs = []
    this.uploadComplete = false
    this.setupDragAndDrop()
  }

  get isMultiple() {
    return this.fileInputTarget.hasAttribute('multiple')
  }

  setupDragAndDrop() {
    this.uploadButtonTarget.addEventListener('dragover', this.handleDragOver.bind(this))
    this.uploadButtonTarget.addEventListener('dragleave', this.handleDragLeave.bind(this))
    this.uploadButtonTarget.addEventListener('drop', this.handleDrop.bind(this))
  }

  handleDragOver(event) {
    event.preventDefault()
    this.uploadButtonTarget.classList.add('border-primary', 'bg-primary', 'bg-opacity-10')
  }

  handleDragLeave(event) {
    event.preventDefault()
    this.uploadButtonTarget.classList.remove('border-primary', 'bg-primary', 'bg-opacity-10')
  }

  handleDrop(event) {
    event.preventDefault()
    this.uploadButtonTarget.classList.remove('border-primary', 'bg-primary', 'bg-opacity-10')
    
    const files = event.dataTransfer.files
    if (files.length > 0) {
      if (this.isMultiple) {
        this.fileInputTarget.files = files
        this.handleMultipleFileSelection(Array.from(files))
      } else {
        const file = files[0]
        if (file.type === 'application/zip' || file.name.endsWith('.zip')) {
          this.fileInputTarget.files = files
          this.handleFileSelection(file)
        } else {
          this.showError('Please select a ZIP file.')
        }
      }
    }
  }

  selectFile() {
    this.fileInputTarget.click()
  }

  fileSelected(event) {
    if (event.target.files.length > 0) {
      if (this.isMultiple) {
        this.handleMultipleFileSelection(Array.from(event.target.files))
      } else {
        this.handleFileSelection(event.target.files[0])
      }
    }
  }

  handleFileSelection(file) {
    this.selectedFiles = [file]
    
    // Update UI to show selected file
    this.selectedFileNameTarget.textContent = file.name
    this.selectedFileSizeTarget.textContent = this.formatFileSize(file.size)
    
    // Hide upload button, show file info
    this.uploadButtonTarget.classList.add('hidden')
    this.fileInfoTarget.classList.remove('hidden')
    this.uploadErrorTarget.classList.add('hidden')
    
    // Start direct upload
    this.startDirectUpload(file)
  }

  handleMultipleFileSelection(files) {
    this.selectedFiles = files
    this.uploadedBlobs = []
    this.totalFiles = files.length
    this.completedFiles = 0
    
    // Update UI to show selected files
    const fileNames = files.map(f => f.name).join(', ')
    const totalSize = files.reduce((sum, f) => sum + f.size, 0)
    this.selectedFileNameTarget.textContent = `${files.length} files selected`
    this.selectedFileSizeTarget.textContent = this.formatFileSize(totalSize)
    
    // Hide upload button, show file info
    this.uploadButtonTarget.classList.add('hidden')
    this.fileInfoTarget.classList.remove('hidden')
    this.uploadErrorTarget.classList.add('hidden')
    
    // Start uploading all files
    this.uploadProgressTarget.classList.remove('hidden')
    this.fileNameTarget.textContent = `Uploading ${files.length} files...`
    this.progressTextTarget.textContent = `Uploading file 1 of ${files.length}...`
    
    this.uploadFilesSequentially(files, 0)
  }

  uploadFilesSequentially(files, index) {
    if (index >= files.length) {
      this.handleMultipleUploadComplete()
      return
    }
    
    const file = files[index]
    this.fileNameTarget.textContent = file.name
    
    const upload = new DirectUpload(file, this.directUploadUrlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const fileProgress = (e.loaded / e.total)
            const overallProgress = ((index + fileProgress) / files.length) * 100
            this.updateProgress(Math.round(overallProgress))
          }
        })
      }
    })
    
    upload.create((error, blob) => {
      if (error) {
        this.handleUploadError(error)
      } else {
        this.uploadedBlobs.push(blob)
        this.completedFiles++
        this.progressTextTarget.textContent = `Uploading file ${this.completedFiles + 1} of ${this.totalFiles}...`
        this.uploadFilesSequentially(files, index + 1)
      }
    })
  }

  handleMultipleUploadComplete() {
    this.uploadComplete = true
    
    // Clear the file input to prevent file submission
    this.fileInputTarget.value = ''
    
    // Create hidden inputs with the signed_ids
    this.uploadedBlobs.forEach((blob) => {
      const hiddenInput = document.createElement('input')
      hiddenInput.type = 'hidden'
      hiddenInput.name = this.inputNameValue + '[]'
      hiddenInput.value = blob.signed_id
      this.formTarget.appendChild(hiddenInput)
    })
    
    // Enable submit button
    this.submitButtonTarget.disabled = false
    
    // Update progress text
    this.progressTextTarget.textContent = `${this.uploadedBlobs.length} files ready to process!`
    this.progressBarTarget.classList.add('bg-success')
  }

  startDirectUpload(file) {
    // Show progress section
    this.uploadProgressTarget.classList.remove('hidden')
    this.fileNameTarget.textContent = file.name
    
    // Create direct upload with progress callback
    const upload = new DirectUpload(file, this.directUploadUrlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentage = Math.round((e.loaded / e.total) * 100)
            this.updateProgress(percentage)
          }
        })
      }
    })
    
    upload.create((error, blob) => {
      if (error) {
        this.handleUploadError(error)
      } else {
        this.handleUploadSuccess(blob)
      }
    })
  }

  updateProgress(percentage) {
    this.progressBarTarget.style.width = percentage + '%'
    this.progressPercentageTarget.textContent = percentage + '%'
    
    if (percentage < 100) {
      this.progressTextTarget.textContent = 'Uploading...'
    } else {
      this.progressTextTarget.textContent = 'Upload complete!'
    }
  }

  handleUploadSuccess(blob) {
    this.uploadComplete = true
    
    // Clear the file input to prevent file submission
    this.fileInputTarget.value = ''
    
    // Create a hidden input with the signed_id
    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = this.inputNameValue
    hiddenInput.value = blob.signed_id
    this.formTarget.appendChild(hiddenInput)
    
    // Enable submit button
    this.submitButtonTarget.disabled = false
    
    // Update progress text
    this.progressTextTarget.textContent = 'Ready to process!'
    this.progressBarTarget.classList.add('bg-success')
  }

  handleUploadError(error) {
    this.showError('Upload failed: ' + error.message)
    
    // Reset UI
    this.uploadProgressTarget.classList.add('hidden')
    this.fileInfoTarget.classList.add('hidden')
    this.uploadButtonTarget.classList.remove('hidden')
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.uploadErrorTarget.classList.remove('hidden')
  }

  clearFile() {
    this.selectedFiles = []
    this.uploadedBlobs = []
    this.uploadComplete = false
    this.fileInputTarget.value = ''
    
    // Reset UI
    this.fileInfoTarget.classList.add('hidden')
    this.uploadProgressTarget.classList.add('hidden')
    this.uploadErrorTarget.classList.add('hidden')
    this.uploadButtonTarget.classList.remove('hidden')
    this.submitButtonTarget.disabled = true
    this.progressBarTarget.classList.remove('bg-success')
    this.progressBarTarget.style.width = '0%'
    this.progressPercentageTarget.textContent = '0%'
    
    // Remove any hidden inputs for this field
    const inputName = this.isMultiple ? this.inputNameValue + '[]' : this.inputNameValue
    const hiddenInputs = this.formTarget.querySelectorAll(`input[name="${inputName}"]`)
    hiddenInputs.forEach(input => input.remove())
  }

  validateSubmission(event) {
    if (!this.uploadComplete) {
      event.preventDefault()
      const message = this.isMultiple 
        ? 'Please wait for all file uploads to complete.'
        : 'Please wait for the file upload to complete.'
      this.showError(message)
      return false
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }
}
